import { Link } from "react-router-dom";
import { SERVICES } from "@/data/site";
import * as LI from "lucide-react";
import { ArrowRight, Check } from "lucide-react";
import Seo, { ORG_SCHEMA, SITE_URL } from "@/components/Seo";

const SERVICES_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: SERVICES.map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Service",
      name: s.title,
      description: s.short,
      url: `${SITE_URL}/services/${s.slug}`,
      provider: { "@id": ORG_SCHEMA["@id"] },
    },
  })),
};

const iconMap = {
  megaphone: LI.Megaphone, monitor: LI.Monitor, kanban: LI.KanbanSquare,
  "share-2": LI.Share2, palette: LI.Palette, lightbulb: LI.Lightbulb,
  shield: LI.ShieldCheck, "graduation-cap": LI.GraduationCap,
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo title="Services" description="Digital marketing, web design, IT project management, social media, graphic design, IT consulting, IT security services and computer training — all under one roof." jsonLd={[ORG_SCHEMA, SERVICES_SCHEMA]} />
      <section className="hero-gradient text-white">
        <div className="container-x py-20 md:py-24 text-center">
          <div className="chip-light mx-auto">Our Services</div>
          <h1 className="mt-4 text-4xl md:text-6xl">Solutions crafted to <span className="text-[color:var(--dh-orange)]">accelerate growth.</span></h1>
          <p className="mt-5 text-white/80 max-w-2xl mx-auto leading-relaxed">
            From digital marketing and web design to IT consulting, IT security services and computer training — we deliver end-to-end solutions built around your goals.
          </p>
        </div>
      </section>

      <section className="container-x py-20 space-y-16">
        {SERVICES.map((s, idx) => {
          const Icon = iconMap[s.icon] || LI.Sparkles;
          const flip = idx % 2 === 1;
          return (
            <div key={s.slug} className={`grid md:grid-cols-2 gap-10 items-center ${flip ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`service-${s.slug}`}>
              <div className="rounded-3xl overflow-hidden border border-[color:var(--dh-line)]">
                <img src={s.image} alt={s.title} className="w-full h-[340px] object-cover" />
              </div>
              <div>
                <div className="w-12 h-12 rounded-xl bg-[color:var(--dh-orange)]/15 text-[color:var(--dh-orange)] grid place-items-center"><Icon className="w-6 h-6" /></div>
                <h2 className="mt-4 text-3xl md:text-4xl text-[color:var(--dh-navy)]">{s.title}</h2>
                <p className="mt-4 text-[color:var(--dh-slate)] leading-relaxed">{s.long}</p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-2 items-start text-sm text-[color:var(--dh-navy)]">
                      <Check className="w-4 h-4 mt-0.5 text-[color:var(--dh-orange)]" /> {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to={`/services/${s.slug}`} className="btn-primary" data-testid={`service-details-${s.slug}`}>View Details <ArrowRight className="w-4 h-4" /></Link>
                  <Link to="/contact" className="btn-ghost-dark" data-testid={`service-cta-${s.slug}`}>Enquire</Link>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="container-x pb-20">
        <div className="rounded-3xl bg-[color:var(--dh-navy)] text-white p-10 md:p-14 text-center">
          <h2 className="text-3xl md:text-4xl">Not sure which service fits your business?</h2>
          <p className="mt-3 text-white/80">Book a free consultation and we'll design a plan for you.</p>
          <Link to="/contact" className="btn-primary mt-6 inline-flex">Talk to an Expert <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
}
